import {
  useSelector,
  type TypedUseSelectorHook,
} from 'react-redux';
import {
  createSelector
} from '@reduxjs/toolkit';
import {
  tournamentsSlice
} from './slice';
import type {
  Tournament,
  TournamentsState,
  TournamentCategory,
  TournamentLeaderboardItem,
  TournamentsWithLeaderboard,
  TournamentLeaderboardListItem,
  TournamentLeaderboardListItemWithCategory,
} from './types';

type RootState = {
  [tournamentsSlice.name]: TournamentsState;
};

export const selectTournaments = (state: RootState): Array<Tournament> => {
  return state[tournamentsSlice.name].privateTournaments ?? [];
};

export const selectActiveTournaments = (state: RootState): Array<Tournament> => {
  return state[tournamentsSlice.name].activeTournaments ?? [];
};

export const selectPendingTournaments = (state: RootState): Array<Tournament> => {
  return state[tournamentsSlice.name].pendingTournaments ?? [];
};

export const selectTournamentsCategories = (state: RootState): Array<TournamentCategory> => {
  return state[tournamentsSlice.name].categories ?? [];
};

export const selectCurrentCategory = (state: RootState): Maybe<TournamentCategory> => {
  return state[tournamentsSlice.name].currentCategory
};

export const selectTournamentLeaderboardsByCategories = (state: RootState): Array<TournamentLeaderboardListItemWithCategory> => {
  return state[tournamentsSlice.name].tournamentLeaderboardListByCategories ?? [];
};

export const selectTournamentLeaderboardByCategory = (category: TournamentCategory) => createSelector(
  [selectTournamentLeaderboardsByCategories],
  (leaderboardListItems): Array<TournamentLeaderboardItem> => leaderboardListItems.find(leaderboardListItems => leaderboardListItems.category === category)?.leaderboardItems ?? []
);

export const selectTournamentByCategory = (category: TournamentCategory) => createSelector(
  [selectTournamentLeaderboardsByCategories, selectTournaments],
  (leaderboardsByCategories, tournaments) => {
    const tournamentIdByCategory = leaderboardsByCategories?.find(leaderboard => leaderboard?.category === category)?.tournamentId;

    return tournaments?.find(tournament => tournament.id === tournamentIdByCategory);
  }
);

export const selectTournamentsLeaderboardList = (state: RootState): Array<TournamentLeaderboardListItem> => {
  return state[tournamentsSlice.name].tournamentsLeaderboardList;
};

export const selectTournamentsWithLeaderboard = createSelector(
  [selectActiveTournaments, selectTournamentsLeaderboardList], (tournaments, tournamentLeaderboardList) => (
    tournaments.reduce<Array<TournamentsWithLeaderboard>>((acc, tournament) => {
      const tournamentLeaderboard = tournamentLeaderboardList
        .find(leaderboard => leaderboard.tournamentId === tournament.id);
      if (tournamentLeaderboard) {
        acc.push({
          tournament,
          leaderboard: tournamentLeaderboard.leaderboardItems
        });
      }
      return acc
    }, [])
  )
);

export const useTournamentsSelector: TypedUseSelectorHook<RootState> =
  useSelector;
